import { values } from 'ramda';
import { useCallback, useMemo } from 'react';
import { Weddings } from '@bridebook/models';
import type { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from '@bridebook/web/lib/utils';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { convertOldEventsToNewEvents } from './date-time-migration';
import { isMainEvent } from '@bridebook/web/lib/events/types';
import { fallbackMainEventName } from '@bridebook/web/lib/wedding-website/utils/fallback-main-event-name';

export const WEDDING_EVENTS_ROOT_QUERY_KEY = 'wedding-events';

export const useWeddingEvents = (mutationFn?: (events: IWeddingEvent[]) => IWeddingEvent[]) => {
  const weddingId = useSelector(getWeddingId);

  const queryKey = useMemo(
    () => [WEDDING_EVENTS_ROOT_QUERY_KEY, 'list', weddingId] as const,
    [weddingId],
  );

  // Base query to fetch initial data
  const { data, isLoading, isError, error } = useQuery<IWeddingEvent[]>(
    queryKey,
    async () => {
      const eventsById = await Weddings._.getById(weddingId).Events.query().get();
      return convertOldEventsToNewEvents(values(eventsById));
    },
    {
      enabled: !!weddingId,
      cacheTime: Infinity,
      staleTime: Infinity,
      select: mutationFn,
    },
  );

  const eventsWithFallback: IWeddingEvent[] = useMemo(
    () =>
      data ? data.map((event) => (isMainEvent(event) ? fallbackMainEventName(event) : event)) : [],
    [data],
  );

  return { data: eventsWithFallback, isLoading, isError, error };
};

export const useInvalidateWeddingEvents = () => {
  const queryClient = useQueryClient();
  const invalidate = useCallback(
    async (weddingId: string) => {
      if (weddingId) {
        await queryClient.invalidateQueries([WEDDING_EVENTS_ROOT_QUERY_KEY, 'list', weddingId]);
      }
    },
    [queryClient],
  );

  return { invalidate };
};

export const updateWeddingEventsCache = (
  queryClient: QueryClient,
  weddingId: string,
  events: IWeddingEvent[],
) => {
  queryClient.setQueryData<IWeddingEvent[]>(
    [WEDDING_EVENTS_ROOT_QUERY_KEY, 'list', weddingId],
    events,
  );
};

export const invalidateEventsQuery = (queryClient: QueryClient, weddingId: string) => {
  queryClient.invalidateQueries([WEDDING_EVENTS_ROOT_QUERY_KEY, 'list', weddingId]);
};
